import { EnumDocumentType } from '@/enums/DocumentType';
import { EnumDocumentNotificationTypes } from '@/enums/EnumDocumentNotificationTypes';
import {
  addTagsMutation,
  archiveDocumentMutation,
  changeDocumentsFolderMutation,
  createDocumentNotification,
  createFolderMutation,
  createTagMutation,
  deleteDocumentNotificationMutation,
  deleteDocumentsMutation,
  deleteFolderMutation,
  deleteNotificationMutation,
  removeTagsMutation,
  updateDocumentOwnerMutation,
  updateFolderMutation,
  updateNotificationMutation,
  editDocumentsMutation,
} from '@/api/contract-management/graphql/contract-management.mutations';
import {
  allTagsQuery,
  contractManagementDocumentsQuery,
  contractManagementKeyValuesQuery,
  fetchDownloadDocuments,
  filterRangesQuery,
  documentNotificationsQuery,
} from '@/api/contract-management/graphql/contract-management.queries';
import { IContractManagementFilter } from '@/contract-management/store/contract-management.store';
import { IContractManagementFilterRanges } from '@/contract-management/types/contract-management-filter-ranges';
import { IContractManagementKeyValues } from '@/contract-management/types/contract-management-key-values';
import { IDocument } from '@/types/Document';
import { IDocumentNotification } from '@/types/DocumentNotification';
import { IDownloadDocument } from '@/types/DownloadDocument';
import { IFolder } from '@/folders/types/folder';
import { ITag } from '@/types/Tag';
import { apolloClient } from '@/apollo';
import { IEditDocumentsPayload } from '@/types/EditDocumentsPayload';
import { IUploadDocument } from '@/types/UploadDocument';
import { showInAllFolders } from '@/contract-management/helpers/contract-management-filter.helper';
import { ICreateNotificationRequest } from '@/types/CreateNotificationRequest';
import { EFetchPolicy } from '@/api/types/enums/fetch-policy';

const loadDocuments = async (
  folderId: string,
  filter: IContractManagementFilter,
  filterActive: boolean
): Promise<IDocument[]> => {
  if (filter.documentType !== EnumDocumentType.Sales) {
    filter = formatCommonFilter(filter);
  }
  const documentTypes: EnumDocumentType[] = getDocumentType(filter.documentType);
  const {
    data: { contractManagement },
  } = await apolloClient.query({
    query: contractManagementDocumentsQuery,
    variables: {
      folderId,
      showInAllFolders: showInAllFolders(filterActive, folderId, filter.search), // Show all documents that matches filter when in root folder
      ...{ ...filter, tags: filter.tags.map((tag: ITag) => tag.id) },
      documentTypes,
    },
    fetchPolicy: EFetchPolicy.NetworkOnly,
  });
  return contractManagement;
};

const loadDownloadUrls = async (documentIds: string[]): Promise<IDownloadDocument[]> => {
  const {
    data: { downloadDocuments },
  } = await apolloClient.query({
    query: fetchDownloadDocuments,
    variables: { documentIds },
  });
  return downloadDocuments;
};

const loadKeyValues = async (
  folderId: string,
  filter: IContractManagementFilter,
  filterActive: boolean
): Promise<IContractManagementKeyValues> => {
  const documentTypes: EnumDocumentType[] = getDocumentType(filter.documentType);
  const {
    data: { contractManagementKeyValues },
  } = await apolloClient.query({
    query: contractManagementKeyValuesQuery,
    variables: {
      folderId,
      showInAllFolders: showInAllFolders(filterActive, folderId, filter.search), // Show all documents that matches filter when in root folder
      ...{ ...filter, tags: filter.tags.map((tag: ITag) => tag.id) },
      documentTypes,
    },
    fetchPolicy: EFetchPolicy.NetworkOnly,
  });
  return contractManagementKeyValues;
};

const uploadDocument = async (payload: IUploadDocument): Promise<IDocument> => {
  const { data } = await apolloClient.mutate({
    mutation: archiveDocumentMutation,
    variables: payload,
  });
  return data.archiveDocument;
};

const createFolder = async (name: string, parentFolderId: string): Promise<IFolder> => {
  const { data } = await apolloClient.mutate({
    mutation: createFolderMutation,
    variables: { name, parentFolderId },
  });
  return data.createFolder;
};

const updateFolderName = async (folderId: string, name: string): Promise<IFolder> => {
  const {
    data: { updateFolder },
  } = await apolloClient.mutate({
    mutation: updateFolderMutation,
    variables: { folderId, name },
  });
  return updateFolder;
};

const deleteFolder = async (id: string): Promise<IFolder> => {
  const { data } = await apolloClient.mutate({
    mutation: deleteFolderMutation,
    variables: { id },
  });
  return data.deleteFolder;
};

const updateDocumentsFolder = async (documentIds: string[], folderId: string): Promise<void> => {
  const {
    data: { bulkMutationResponse },
  } = await apolloClient.mutate({
    mutation: changeDocumentsFolderMutation,
    variables: { documentIds, folderId },
  });
  return bulkMutationResponse;
};

const deleteDocuments = async (ids: string[]): Promise<IDocument[]> => {
  const { data } = await apolloClient.mutate({
    mutation: deleteDocumentsMutation,
    variables: { ids },
  });
  return data.deleteDocuments;
};

const editDocuments = async ({
  payload,
  documentIds,
}: {
  payload: IEditDocumentsPayload;
  documentIds: string[];
}): Promise<IDocument[]> => {
  const {
    data: { updateDocuments },
  } = await apolloClient.mutate({
    mutation: editDocumentsMutation,
    variables: { ...payload, documentIds },
  });
  return updateDocuments;
};

const updateDocumentOwner = async (documentId: string, userId: string): Promise<IDocument> => {
  const { data } = await apolloClient.mutate({
    mutation: updateDocumentOwnerMutation,
    variables: { documentId, userId },
  });
  return data.updateDocumentOwner;
};

const addDocumentTags = async (documentIds: string[], tagIds: string[]): Promise<string[]> => {
  const { data } = await apolloClient.mutate({
    mutation: addTagsMutation,
    variables: { documentIds, tagIds },
  });
  return data.addDocumentTags;
};

const deleteDocumentTags = async (documentIds: string[], tagIds: string[]): Promise<boolean> => {
  const {
    data: { removeDocumentTags },
  } = await apolloClient.mutate({
    mutation: removeTagsMutation,
    variables: { documentIds, tagIds },
  });
  return removeDocumentTags;
};

const updateNotification = async (
  id: string,
  userId: string,
  documentId: string,
  notificationType: EnumDocumentNotificationTypes,
  notificationNote: string,
  notificationDate: Date
): Promise<IDocumentNotification> => {
  const { data } = await apolloClient.mutate({
    mutation: updateNotificationMutation,
    variables: {
      id,
      userId,
      documentId,
      notificationType,
      notificationNote,
      notificationDate,
    },
  });
  return data.updateNotification;
};

const deleteDocumentNotification = async (id: string): Promise<IDocumentNotification> => {
  const { data } = await apolloClient.mutate({
    mutation: deleteNotificationMutation,
    variables: {
      id,
    },
  });
  return data.deleteDocumentNotification;
};

const loadFilterRanges = async (): Promise<IContractManagementFilterRanges> => {
  const {
    data: { contractManagementFilterRanges },
  } = await apolloClient.query({
    query: filterRangesQuery,
  });
  return verifyFilterRanges(contractManagementFilterRanges);
};

const loadAllTags = async (): Promise<ITag[]> => {
  const {
    data: { entity },
  } = await apolloClient.query({
    query: allTagsQuery,
    fetchPolicy: EFetchPolicy.NoCache,
  });
  return entity.tags;
};

const createTag = async (title: string): Promise<ITag> => {
  const { data } = await apolloClient.mutate({
    mutation: createTagMutation,
    variables: { title },
  });
  return data.createTag;
};

const loadDocumentNotifications = async (): Promise<IDocumentNotification[]> => {
  const {
    data: { documentNotifications },
  } = await apolloClient.query({
    query: documentNotificationsQuery,
  });
  return documentNotifications;
};

const createNotifications = async (
  notifications: ICreateNotificationRequest[]
): Promise<IDocumentNotification[]> => {
  return Promise.all(
    notifications.map(async (notification: ICreateNotificationRequest) => {
      const { data } = await apolloClient.mutate({
        mutation: createDocumentNotification,
        variables: {
          documentIds: [notification.documentId],
          notificationDate: notification.notificationDate,
          notificationNote: notification.notificationNote,
          notificationType: notification.notificationType,
          userId: notification.userId,
        },
      });
      return data.createDocumentNotification[0];
    })
  );
};

const deleteNotifications = async (ids: string[]): Promise<void> => {
  await apolloClient.mutate({
    mutation: deleteDocumentNotificationMutation,
    variables: { ids },
  });
};

function verifyFilterRanges(
  filterRanges: IContractManagementFilterRanges
): IContractManagementFilterRanges {
  return {
    minValue: filterRanges.minValue,
    maxValue: filterRanges.maxValue,
    minContractLength: filterRanges.minContractLength,
    maxContractLength: filterRanges.maxContractLength,
    minContractEndDate: filterRanges.minContractEndDate,
    maxContractEndDate: filterRanges.maxContractEndDate,
  };
}

function formatCommonFilter(filter: IContractManagementFilter) {
  return {
    ...filter,
    value: null,
    contractEndDate: [null, null],
    signDate: [null, null],
    contractLength: null,
  };
}

function getDocumentType(documentType: EnumDocumentType) {
  if (documentType === EnumDocumentType.Other) {
    return [EnumDocumentType.Other, EnumDocumentType.Document];
  }
  return [];
}

export default {
  addDocumentTags,
  updateFolderName,
  updateDocumentsFolder,
  createFolder,
  deleteDocumentNotification,
  deleteFolder,
  loadDocuments,
  loadDownloadUrls,
  loadFilterRanges,
  loadKeyValues,
  loadAllTags,
  deleteDocumentTags,
  updateDocumentOwner,
  updateNotification,
  uploadDocument,
  deleteDocuments,
  editDocuments,
  createTag,
  createNotifications,
  deleteNotifications,
  loadDocumentNotifications,
};
